import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import {
  selectIsAuthenticated,
  selectCurrentUser,
} from '../../features/auth/authSlice';
import {
  useGetPropertyNeedsQuery,
  useCreatePropertyNeedMutation,
} from '../../features/api/apiSlice';
import { isProfessionalRole } from '../../lib/authPortals';
import UserLayout from '../user-dashboard/UserLayout';
import AgentLayout from '../agent-dashboard/AgentLayout';
import { trackEvent } from '../../lib/analytics';
import PlatformAdBanner from '../../components/monetization/PlatformAdBanner';

const TRANSACTION_OPTIONS = [
  { value: '', label: 'All transactions' },
  { value: 'buy', label: 'Buy' },
  { value: 'rent', label: 'Rent' },
  { value: 'lease', label: 'Lease' },
  { value: 'invest', label: 'Invest' },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'house', label: 'House' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'land', label: 'Land' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'other', label: 'Other' },
];

const URGENCY_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Flexible' },
  { value: 'high', label: 'Soon' },
  { value: 'urgent', label: 'Urgent' },
];

const TX_FORM_OPTIONS = TRANSACTION_OPTIONS.filter((o) => o.value);
const TYPE_FORM_OPTIONS = PROPERTY_TYPE_OPTIONS.filter((o) => o.value);

const formatBudget = (min, max, currency = 'UGX') => {
  if (min == null && max == null) return 'Budget flexible';
  const fmt = (n) =>
    n != null ? `${currency} ${Number(n).toLocaleString()}` : null;
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`;
  if (min != null) return `From ${fmt(min)}`;
  return `Up to ${fmt(max)}`;
};

const urgencyStyles = {
  low: 'bg-slate-100 text-slate-600 border-slate-200',
  normal: 'bg-sky-50 text-sky-700 border-sky-100',
  high: 'bg-amber-50 text-amber-800 border-amber-100',
  urgent: 'bg-rose-50 text-rose-700 border-rose-100',
};

const txStyles = {
  rent: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  buy: 'bg-violet-50 text-violet-700 border-violet-100',
  lease: 'bg-cyan-50 text-cyan-800 border-cyan-100',
  invest: 'bg-orange-50 text-orange-800 border-orange-100',
};

const emptyForm = {
  title: '',
  description: '',
  transaction_type: 'rent',
  property_type: 'apartment',
  budget_min: '',
  budget_max: '',
  currency: 'UGX',
  location: '',
  city: '',
  area: '',
  bedrooms: '',
  bathrooms: '',
  amenities: '',
  urgency: 'normal',
};

const NoticeBoardContent = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const isPro = isProfessionalRole(user?.account_role);

  const [filters, setFilters] = useState({
    transaction_type: '',
    property_type: '',
    city: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const queryParams = useMemo(
    () => ({
      limit: 30,
      ...(filters.transaction_type
        ? { transaction_type: filters.transaction_type }
        : {}),
      ...(filters.property_type ? { property_type: filters.property_type } : {}),
      ...(filters.city ? { city: filters.city } : {}),
    }),
    [filters]
  );

  const { data, isLoading, isFetching, refetch } =
    useGetPropertyNeedsQuery(queryParams);
  const [createNeed, { isLoading: isCreating }] =
    useCreatePropertyNeedMutation();

  const needs = data?.data || [];

  const handlePostClick = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/notice-board' } } });
      return;
    }
    setShowForm(true);
    setFormError('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    if (!form.title.trim()) {
      setFormError('Title is required');
      return;
    }
    try {
      const body = {
        ...form,
        budget_min: form.budget_min === '' ? null : Number(form.budget_min),
        budget_max: form.budget_max === '' ? null : Number(form.budget_max),
        bedrooms: form.bedrooms === '' ? null : Number(form.bedrooms),
        bathrooms: form.bathrooms === '' ? null : Number(form.bathrooms),
        location:
          form.location ||
          [form.area, form.city].filter(Boolean).join(', '),
      };
      const res = await createNeed(body).unwrap();
      if (res?.error) {
        setFormError(res.message || 'Failed to post');
        return;
      }
      trackEvent('notice_board_post', { title: form.title }, 'lead');
      setFormSuccess(res.message || 'Your request is live on the notice board.');
      setShowForm(false);
      setForm(emptyForm);
      refetch();
    } catch (err) {
      setFormError(
        err?.data?.message || 'Failed to post your specific property request.'
      );
    }
  };

  const clearFilters = () =>
    setFilters({ transaction_type: '', property_type: '', city: '' });

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 text-white p-6 sm:p-8 shadow-lg">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-sky-400/10 blur-2xl pointer-events-none" />
        <div className="absolute -left-8 bottom-0 w-32 h-32 rounded-full bg-violet-500/10 blur-2xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-300 mb-2">
              Notice board · Collaboration
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
              Property needs, matched by pros
            </h1>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Seekers post exactly what they need. Verified agents, brokers, and
              advisors respond with matches — clear, in-platform, no noise.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {[
                { icon: 'ShieldCheck', t: 'Verified responders' },
                { icon: 'MessageSquare', t: 'Direct proposals' },
                { icon: 'Zap', t: 'Faster matching' },
              ].map((b) => (
                <span
                  key={b.t}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-slate-100"
                >
                  <Icon name={b.icon} size={13} />
                  {b.t}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            {!isPro && (
              <Button
                onClick={handlePostClick}
                iconName="Plus"
                iconPosition="left"
                className="!bg-white !text-slate-900 hover:!bg-slate-100 shadow-md"
              >
                Specific Property Request
              </Button>
            )}
            {isPro && (
              <div className="rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-sm text-slate-200 max-w-xs">
                <p className="font-semibold text-white mb-0.5">Agent mode</p>
                <p className="text-xs text-slate-300">
                  Browse open needs and send proposals. Verified agents rank
                  higher with clients.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {formSuccess && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm">
          <Icon name="CheckCircle2" size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Request published</p>
            <p className="text-emerald-700/90">{formSuccess}</p>
          </div>
        </div>
      )}

      {/* Post form */}
      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 bg-slate-50/80">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                What are you looking for?
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Clear details get better agent responses.
              </p>
            </div>
            <button
              type="button"
              className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-white transition-colors"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
          <div className="p-5 sm:p-6 space-y-4">
            {formError && (
              <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                {formError}
              </p>
            )}
            <Input
              label="Title"
              placeholder="e.g. 3-bedroom house in Wakiso under 300M"
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              required
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Description
              </label>
              <textarea
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-400"
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
                placeholder="Must-haves, timing, neighbourhood preferences…"
              />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Select
                label="Transaction"
                options={TX_FORM_OPTIONS}
                value={form.transaction_type}
                onChange={(v) =>
                  setForm((p) => ({ ...p, transaction_type: v }))
                }
              />
              <Select
                label="Property type"
                options={TYPE_FORM_OPTIONS}
                value={form.property_type}
                onChange={(v) => setForm((p) => ({ ...p, property_type: v }))}
              />
              <Select
                label="Urgency"
                options={URGENCY_OPTIONS}
                value={form.urgency}
                onChange={(v) => setForm((p) => ({ ...p, urgency: v }))}
              />
              <Input
                label="City"
                value={form.city}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                placeholder="Kampala"
              />
              <Input
                label="Area / neighbourhood"
                value={form.area}
                onChange={(e) => setForm((p) => ({ ...p, area: e.target.value }))}
                placeholder="Kololo, Ntinda…"
              />
              <Input
                label="Bedrooms"
                type="number"
                value={form.bedrooms}
                onChange={(e) =>
                  setForm((p) => ({ ...p, bedrooms: e.target.value }))
                }
              />
              <Input
                label="Budget min (UGX)"
                type="number"
                value={form.budget_min}
                onChange={(e) =>
                  setForm((p) => ({ ...p, budget_min: e.target.value }))
                }
              />
              <Input
                label="Budget max (UGX)"
                type="number"
                value={form.budget_max}
                onChange={(e) =>
                  setForm((p) => ({ ...p, budget_max: e.target.value }))
                }
              />
              <Input
                label="Bathrooms"
                type="number"
                value={form.bathrooms}
                onChange={(e) =>
                  setForm((p) => ({ ...p, bathrooms: e.target.value }))
                }
              />
            </div>
            <Input
              label="Amenities / requirements"
              value={form.amenities}
              onChange={(e) =>
                setForm((p) => ({ ...p, amenities: e.target.value }))
              }
              placeholder="Parking, compound, water, title deed…"
            />
            <div className="flex flex-wrap gap-3 pt-1">
              <Button type="submit" loading={isCreating} disabled={isCreating}>
                Publish request
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1 grid sm:grid-cols-3 gap-3">
            <Select
              options={TRANSACTION_OPTIONS}
              value={filters.transaction_type}
              onChange={(v) =>
                setFilters((p) => ({ ...p, transaction_type: v }))
              }
              placeholder="Transaction"
            />
            <Select
              options={PROPERTY_TYPE_OPTIONS}
              value={filters.property_type}
              onChange={(v) => setFilters((p) => ({ ...p, property_type: v }))}
              placeholder="Type"
            />
            <Input
              placeholder="Filter by city"
              value={filters.city}
              onChange={(e) =>
                setFilters((p) => ({ ...p, city: e.target.value }))
              }
            />
          </div>
          <button
            type="button"
            onClick={clearFilters}
            className="text-sm font-medium text-slate-500 hover:text-slate-800 px-2 py-2"
          >
            Clear
          </button>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500">
            {isLoading || isFetching
              ? 'Updating…'
              : `${needs.length} open request${needs.length === 1 ? '' : 's'}`}
          </p>
          {!isPro && !showForm && (
            <button
              type="button"
              onClick={handlePostClick}
              className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
            >
              <Icon name="Plus" size={14} />
              New request
            </button>
          )}
        </div>
      </div>

      <PlatformAdBanner placement="notice_board" />

      {/* List */}
      {isLoading || isFetching ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-36 rounded-2xl bg-slate-100 animate-pulse border border-slate-200"
            />
          ))}
        </div>
      ) : needs.length === 0 ? (
        <div className="text-center py-16 px-6 rounded-2xl border border-dashed border-slate-200 bg-white">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-violet-50 flex items-center justify-center">
            <Icon name="ClipboardList" size={26} className="text-violet-500" />
          </div>
          <p className="text-lg font-semibold text-slate-900 mb-1">
            No open needs yet
          </p>
          <p className="text-sm text-slate-500 mb-5 max-w-md mx-auto">
            Be the first to describe what you need — agents will respond with
            matching options.
          </p>
          {!isPro && (
            <Button onClick={handlePostClick} iconName="Plus" iconPosition="left">
              Specific Property Request
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {needs.map((need) => {
            const tx = String(need.transaction_type || '').toLowerCase();
            const urg = String(need.urgency || 'normal').toLowerCase();
            const replies = need.response_count ?? need.responses?.length ?? 0;
            return (
              <Link
                key={need.id}
                to={`/notice-board/${need.id}`}
                className="group block bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 hover:shadow-md transition-all"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2.5">
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${
                          txStyles[tx] || 'bg-slate-50 text-slate-600 border-slate-200'
                        }`}
                      >
                        {need.transaction_type || 'need'}
                      </span>
                      {need.property_type && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                          {need.property_type}
                        </span>
                      )}
                      <span
                        className={`text-[10px] font-semibold capitalize px-2.5 py-1 rounded-full border ${
                          urgencyStyles[urg] || urgencyStyles.normal
                        }`}
                      >
                        {need.urgency || 'normal'}
                      </span>
                      {need.status && need.status !== 'open' && (
                        <span className="text-[10px] font-semibold uppercase px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                          {need.status}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900 group-hover:text-sky-800 transition-colors mb-1">
                      {need.title}
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3 leading-relaxed">
                      {need.description || 'No description provided.'}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs sm:text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="MapPin" size={14} className="text-slate-400" />
                        {need.city ||
                          need.location ||
                          need.area ||
                          'Location flexible'}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="Wallet" size={14} className="text-slate-400" />
                        {formatBudget(
                          need.budget_min,
                          need.budget_max,
                          need.currency
                        )}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Icon
                          name="MessageSquare"
                          size={14}
                          className="text-slate-400"
                        />
                        {replies} {replies === 1 ? 'reply' : 'replies'}
                      </span>
                      {(need.bedrooms != null || need.bathrooms != null) && (
                        <span className="inline-flex items-center gap-1.5">
                          <Icon name="Bed" size={14} className="text-slate-400" />
                          {need.bedrooms ?? '—'} bed · {need.bathrooms ?? '—'} bath
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="sm:text-right shrink-0 flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 border-t sm:border-t-0 border-slate-100 pt-3 sm:pt-0">
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {need.customer?.name || 'User'}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {need.created_at
                          ? new Date(need.created_at).toLocaleDateString(
                              undefined,
                              {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              }
                            )
                          : ''}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 group-hover:gap-1.5 transition-all">
                      View
                      <Icon name="ArrowRight" size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {isPro && (
        <div className="rounded-2xl border border-amber-100 bg-amber-50/80 px-5 py-4 text-sm text-amber-900">
          <p className="font-semibold mb-1">Responding as a professional</p>
          <p className="text-amber-800/90 text-xs sm:text-sm leading-relaxed">
            Open any request to send a proposal. Verified status builds trust
            with seekers.
            {user?.verification_status !== 'verified' && (
              <>
                {' '}
                <Link
                  to="/agent-dashboard/verification"
                  className="font-semibold underline underline-offset-2"
                >
                  Complete verification
                </Link>
                .
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

/** Public site chrome when not inside a dashboard panel. */
const PublicShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex flex-col">
    <Header />
    <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      {children}
    </main>
    <Footer />
  </div>
);

const NoticeBoardPage = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);
  const isPro = isProfessionalRole(user?.account_role);

  const content = <NoticeBoardContent />;

  // Logged-in professionals → agent panel chrome
  if (isAuthenticated && isPro) {
    return (
      <AgentLayout
        title="Notice Board"
        subtitle="Respond to live property needs from seekers"
      >
        {content}
      </AgentLayout>
    );
  }

  // Logged-in customers → user dashboard chrome
  if (isAuthenticated) {
    return (
      <UserLayout
        title="Notice Board"
        subtitle="Post requests and track agent responses"
      >
        {content}
      </UserLayout>
    );
  }

  return <PublicShell>{content}</PublicShell>;
};

export default NoticeBoardPage;
